import { useState, useRef } from 'react';
import { Box, DialogActions, Typography } from '@mui/material';
import DialogBox from '../../../core-components/theme/components/DialogBox/DialogBox';
import { useAddDataLogger } from '../../DataLogger/hooks/useAddDataLogger';
import SaveCancelButtons from '../../../core-components/theme/components/SaveCancelComponent/SaveCancelButtons';

interface AddDataLoggerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (data: { serialNumber: string }) => void;
}

export default function AddDataLoggerDialog({ open, onClose, onSuccess }: AddDataLoggerDialogProps) {
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { handleSubmit, isPending, error, data } = useAddDataLogger();

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleSubmitClick();
    }
  };

  const handleSubmitClick = () => {
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      handleSubmit({ code: fullCode });
    }
  };

  const handleCancel = () => {
    setCode(Array(6).fill(''));
    onClose();
  };

  const handleDialogClose = () => {
    setCode(Array(6).fill(''));
    onClose();
  };

  const handleSuccessAndClose = () => {
    if (data?.data?.serialNumber) {
      onSuccess({ serialNumber: data.data.serialNumber });
      setCode(Array(6).fill(''));
      onClose();
    }
  };

  const renderInputs = () => {
    const inputs = [];
    for (let i = 0; i < 6; i++) {
      inputs.push(
        <input
          key={i}
          ref={el => {
            inputRefs.current[i] = el;
          }}
          type="text"
          maxLength={1}
          value={code[i]}
          onChange={e => handleInputChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          style={{
            width: '40px',
            height: '40px',
            textAlign: 'center',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            border: '1px solid #ccc',
            borderRadius: '8px',
            backgroundColor: 'white',
            outline: 'none',
          }}
        />
      );
    }
    return inputs;
  };

  return (
    <DialogBox
      open={open}
      handleClose={handleDialogClose}
      title="Add Data Logger"
      maxWidth="sm"
      minWidth="400px"
      padding="24px"
      centerTitle
    >
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
          {renderInputs()}
        </Box>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please enter the 6-digit code
        </Typography>
        {error && (
          <Typography variant="body2" color="error" textAlign="center" sx={{ mt: 1 }}>
            {error.message}
          </Typography>
        )}
      </Box>

      <DialogActions sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 0 }}>
        <SaveCancelButtons
          onSave={handleSubmitClick}
          onCancel={handleCancel}
          loading={isPending}
          customSaveDisabled={code.join('').length !== 6}
          cancelButtonStyle={{
            width: { xs: '50%', sm: '50%', md: '35%' },
          }}
          saveButtonStyle={{
            width: { xs: '50%', sm: '50%', md: '35%' },
          }}
        />
      </DialogActions>

      {data?.data?.serialNumber && handleSuccessAndClose()}
    </DialogBox>
  );
} 